import { useMemo, useState } from "react";
import { useGetList, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import type { CampaignTargetDraft, CampaignTargetType, Contact } from "../types";
import { targetDraftKey } from "./campaignTargets";

const formatContactLabel = (contact: Contact) => {
  const name = `${contact.first_name ?? ""} ${contact.last_name ?? ""}`.trim();
  if (contact.company_name) {
    return name ? `${name} (${contact.company_name})` : contact.company_name;
  }
  return name || `Contact #${contact.id}`;
};

export const CampaignTargetInput = ({
  targets,
  onChange,
}: {
  targets: CampaignTargetDraft[];
  onChange: (targets: CampaignTargetDraft[]) => void;
}) => {
  const translate = useTranslate();
  const [targetType, setTargetType] = useState<CampaignTargetType>("segment");
  const [segmentId, setSegmentId] = useState<number | "">("");
  const [companyId, setCompanyId] = useState<number | "">("");
  const [contactId, setContactId] = useState<number | "">("");
  const [tagId, setTagId] = useState<number | "">("");

  const { data: segments } = useGetList("bc_segments", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "description", order: "ASC" },
  });

  const { data: companies } = useGetList("companies", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "name", order: "ASC" },
  });

  const { data: contacts } = useGetList("contacts", {
    pagination: { page: 1, perPage: 500 },
    sort: { field: "last_name", order: "ASC" },
  });

  const { data: tags } = useGetList("tags", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "name", order: "ASC" },
  });

  const companyNameById = useMemo(
    () => new Map(companies?.map((c) => [c.id, c.name]) ?? []),
    [companies],
  );

  const contactLabelById = useMemo(
    () => new Map(contacts?.map((c) => [c.id, formatContactLabel(c)]) ?? []),
    [contacts],
  );

  const tagNameById = useMemo(
    () => new Map(tags?.map((t) => [t.id, t.name]) ?? []),
    [tags],
  );

  const addTarget = () => {
    let draft: CampaignTargetDraft | null = null;
    if (targetType === "segment" && segmentId) {
      draft = {
        target_type: "segment",
        bc_segment_id: segmentId,
        company_id: null,
        contact_id: null,
        tag_id: null,
      };
    } else if (targetType === "company" && companyId) {
      draft = {
        target_type: "company",
        company_id: companyId,
        bc_segment_id: null,
        contact_id: null,
        tag_id: null,
      };
    } else if (targetType === "contact" && contactId) {
      draft = {
        target_type: "contact",
        contact_id: contactId,
        bc_segment_id: null,
        company_id: null,
        tag_id: null,
      };
    } else if (targetType === "tag" && tagId) {
      draft = {
        target_type: "tag",
        tag_id: tagId,
        bc_segment_id: null,
        company_id: null,
        contact_id: null,
      };
    }
    if (!draft) return;
    const key = targetDraftKey(draft);
    const exists = targets.some((t) => targetDraftKey(t) === key);
    if (!exists) onChange([...targets, draft]);
    setSegmentId("");
    setCompanyId("");
    setContactId("");
    setTagId("");
  };

  const removeAt = (index: number) => {
    onChange(targets.filter((_, i) => i !== index));
  };

  const labelFor = (t: CampaignTargetDraft) => {
    if (t.target_type === "segment") {
      const seg = segments?.find((s) => s.id === t.bc_segment_id);
      return `Segment: ${seg?.description ?? seg?.number ?? t.bc_segment_id}`;
    }
    if (t.target_type === "company") {
      return `Company: ${companyNameById.get(t.company_id!) ?? t.company_id}`;
    }
    if (t.target_type === "tag") {
      return `Tag: ${tagNameById.get(t.tag_id!) ?? t.tag_id}`;
    }
    return `Contact: ${contactLabelById.get(t.contact_id!) ?? t.contact_id}`;
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-sm font-medium">
        {translate("resources.campaigns.fields.targets", { _: "Targets" })}
      </span>

      <div className="flex flex-wrap gap-2">
        {targets.map((t, i) => (
          <Badge key={targetDraftKey(t)} variant="secondary" className="gap-1">
            {labelFor(t)}
            <button
              type="button"
              className="ml-1 hover:text-destructive"
              onClick={() => removeAt(i)}
            >
              ×
            </button>
          </Badge>
        ))}
      </div>

      <select
        className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
        value={targetType}
        onChange={(e) => setTargetType(e.target.value as CampaignTargetType)}
      >
        <option value="segment">
          {translate("resources.campaigns.targets.types.segment", {
            _: "BC Segment",
          })}
        </option>
        <option value="company">
          {translate("resources.campaigns.targets.types.company", {
            _: "Company",
          })}
        </option>
        <option value="contact">
          {translate("resources.campaigns.targets.types.contact", {
            _: "Contact",
          })}
        </option>
        <option value="tag">
          {translate("resources.campaigns.targets.types.tag", { _: "Tag" })}
        </option>
      </select>

      {targetType === "segment" && (
        <select
          className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
          value={segmentId}
          onChange={(e) =>
            setSegmentId(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">
            {translate("resources.campaigns.targets.select_segment", {
              _: "Select segment…",
            })}
          </option>
          {segments?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.description ?? s.number}
            </option>
          ))}
        </select>
      )}

      {targetType === "company" && (
        <select
          className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
          value={companyId}
          onChange={(e) =>
            setCompanyId(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">
            {translate("resources.campaigns.targets.select_company", {
              _: "Select company…",
            })}
          </option>
          {companies?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}

      {targetType === "contact" && (
        <select
          className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
          value={contactId}
          onChange={(e) =>
            setContactId(e.target.value ? Number(e.target.value) : "")
          }
        >
          <option value="">
            {translate("resources.campaigns.targets.select_contact", {
              _: "Select contact…",
            })}
          </option>
          {contacts?.map((c) => (
            <option key={c.id} value={c.id}>
              {formatContactLabel(c)}
            </option>
          ))}
        </select>
      )}

      {targetType === "tag" && (
        <select
          className="flex h-9 w-full rounded-md border border-input px-3 py-1 text-sm"
          value={tagId}
          onChange={(e) => setTagId(e.target.value ? Number(e.target.value) : "")}
        >
          <option value="">
            {translate("resources.campaigns.targets.select_tag", {
              _: "Select tag…",
            })}
          </option>
          {tags?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      )}

      <Button type="button" variant="outline" size="sm" onClick={addTarget}>
        {translate("resources.campaigns.targets.add", { _: "Add target" })}
      </Button>
    </div>
  );
};
